/**
 * Renderer-agnostic authoring state machine for the measured floor plan.
 *
 * Every gesture the organizer makes is a pure transition over `(state, document, event)`, and a
 * renderer supplies only world-space pointer coordinates plus its current scale. That keeps direct
 * manipulation (drag-to-draw, handles, marquee, copy and paste) testable without a DOM, and lets
 * the SVG canvas be swapped for a canvas-backed one without touching this file or the domain model.
 */
import {
  addArea,
  addTable,
  duplicateAreas,
  duplicateTables,
  type FloorPlanArea,
  type FloorPlanArea as PlanArea,
  type FloorPlanDocument,
  type FloorPlanPoint,
  type FloorPlanShape,
  type FloorPlanTable,
  isAxisAlignedRectangle,
  moveAreas,
  moveAreaVertex,
  moveTables,
  pointInPolygon,
  rectangleBoundary,
  removeAreas,
  removeTables,
  repeatTables,
  reshapedAreaPolygon,
  resizeTables,
  rotateTable,
  shapePolygon,
  SIXTEENTHS_PER_FOOT,
  snapRowAngle,
  tablePolygon,
  tablesBounds,
} from "@/lib/shows/floorPlan";

export const GRID = SIXTEENTHS_PER_FOOT;

/**
 * Screen pixels within which the first corner of a trace reclaims the pointer. Generous on
 * purpose, and larger than a handle: closing the shape is the one move that ENDS the gesture, and
 * a finger has no hover to aim with.
 */
const CLOSE_PIXELS = 16;

/** Screen pixels within which a vertex handle answers to the pointer. */
const HANDLE_PIXELS = 12;

/**
 * Screen pixels between a shape and the controls that hang off it. In PIXELS because that is what
 * the organizer is aiming at: a gap measured in feet shrinks to nothing on a narrow pane, and a
 * control the width of a hair is a control nobody can see, let alone grab.
 */
export const CONTROL_GAP_PIXELS = 14;

/**
 * The repeat handles' own, larger standoff. Resize now sits ON the table's corners and rotate
 * just off them, at `CONTROL_GAP_PIXELS`; repeat stands further out again so a picked table shows
 * three genuinely separate rings of controls, nearest to farthest, rather than two crowding a
 * third.
 */
export const REPEAT_CONTROL_GAP_PIXELS = CONTROL_GAP_PIXELS * 2.5;

const bearing = (center: FloorPlanPoint, point: FloorPlanPoint) =>
  (Math.atan2(point.y - center.y, point.x - center.x) * 180) / Math.PI;

/**
 * Where the rotate control sits: just off the table's upper-right corner, in the table's own
 * frame, so it travels with the table as it turns. Off the corner rather than centred above,
 * because the top edge carries the Vendor badge and the number has to stay the loudest thing on
 * the table.
 */
export function rotationControl(table: FloorPlanTable, clear: number): FloorPlanPoint {
  const radians = (table.rotation * Math.PI) / 180;
  const across = Math.cos(radians);
  const down = Math.sin(radians);
  const out = table.width / 2 + clear;
  const up = table.depth / 2 + clear;
  return {
    x: Math.round(table.center.x + out * across + up * down),
    y: Math.round(table.center.y + out * down - up * across),
  };
}

/**
 * The one table whose rotate control is under the pointer. Offered for a single unlocked table
 * only: with several picked there is no one thing the control belongs to, and a pinned table is
 * pinned against turning too.
 */
export function rotationGrabAt(
  document: FloorPlanDocument,
  point: FloorPlanPoint,
  unitsPerPixel: number,
  selected: readonly string[],
): FloorPlanTable | null {
  if (selected.length !== 1) return null;
  const table = document.tables.find((candidate) => candidate.id === selected[0]);
  if (!table) return null;
  const control = rotationControl(table, CONTROL_GAP_PIXELS * unitsPerPixel);
  const reach = HANDLE_PIXELS * unitsPerPixel;
  return Math.hypot(control.x - point.x, control.y - point.y) <= reach ? table : null;
}

/**
 * Where the four resize handles sit: the table's own four corners, in the table's own frame, so
 * they travel and turn with it exactly as the rotate control does. Same order `tablePolygon` draws
 * them in, so a caller pairing the two up (a handle and the corner it sits on) never has to guess.
 */
export function resizeControls(table: FloorPlanTable): readonly FloorPlanPoint[] {
  const radians = (table.rotation * Math.PI) / 180;
  const across = { x: Math.cos(radians), y: Math.sin(radians) };
  const forward = { x: -Math.sin(radians), y: Math.cos(radians) };
  const halfWidth = table.width / 2;
  const halfDepth = table.depth / 2;
  const corner = (acrossSign: 1 | -1, forwardSign: 1 | -1): FloorPlanPoint => ({
    x: Math.round(
      table.center.x + acrossSign * halfWidth * across.x + forwardSign * halfDepth * forward.x,
    ),
    y: Math.round(
      table.center.y + acrossSign * halfWidth * across.y + forwardSign * halfDepth * forward.y,
    ),
  });
  return [corner(-1, -1), corner(1, -1), corner(1, 1), corner(-1, 1)];
}

/**
 * The resize handle under the pointer, and which corner it is - for a single unlocked table, like
 * the rotate ring: there is no one table for several handles to belong to, and a pinned table is
 * pinned against resizing too.
 */
export function resizeGrabAt(
  document: FloorPlanDocument,
  point: FloorPlanPoint,
  unitsPerPixel: number,
  selected: readonly string[],
): { table: FloorPlanTable; corner: number; } | null {
  if (selected.length !== 1) return null;
  const table = document.tables.find((candidate) => candidate.id === selected[0]);
  if (!table) return null;
  const reach = HANDLE_PIXELS * unitsPerPixel;
  const corners = resizeControls(table);
  for (let index = 0; index < corners.length; index += 1) {
    const at = corners[index];
    if (Math.hypot(at.x - point.x, at.y - point.y) <= reach) return { table, corner: index };
  }
  return null;
}

/**
 * The vertex handle of the single selected area under the pointer, and which corner it is - an
 * area has no fixed width or depth, so every corner of its own polygon gets a handle rather than
 * the table's fixed four. Offered only for one area picked alone: with several picked, or a table
 * picked alongside, there is no one shape for a handle to belong to.
 */
export function areaVertexGrabAt(
  document: FloorPlanDocument,
  point: FloorPlanPoint,
  unitsPerPixel: number,
  selection: FloorPlanSelection,
): { area: FloorPlanArea; index: number; } | null {
  if (selection.areas.length !== 1 || selection.tables.length !== 0) return null;
  const area = document.areas.find((candidate) => candidate.id === selection.areas[0]);
  if (!area) return null;
  const reach = HANDLE_PIXELS * unitsPerPixel;
  for (let index = 0; index < area.polygon.length; index += 1) {
    const vertex = area.polygon[index];
    if (Math.hypot(vertex.x - point.x, vertex.y - point.y) <= reach) return { area, index };
  }
  return null;
}

/** A world point's offset from a table's centre, in the table's own frame: `across` runs with its
 * width, `forward` with its depth - the same axes `tablePolygon` draws corners along. */
function localOffset(
  center: FloorPlanPoint,
  rotationDegrees: number,
  point: FloorPlanPoint,
): { across: number; forward: number; } {
  const radians = (rotationDegrees * Math.PI) / 180;
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return {
    across: dx * Math.cos(radians) + dy * Math.sin(radians),
    forward: dx * -Math.sin(radians) + dy * Math.cos(radians),
  };
}

/**
 * Snaps a live resize drag to the nearest whole foot, on both sides of centre at once (the
 * handle's offset from centre is half the table's span), and never lets a table shrink below one.
 * `halfSpan` takes its sign from whichever corner is grabbed; resizing is centred on the table
 * regardless, so only its size matters.
 */
function snappedSpan(halfSpan: number): number {
  return Math.max(GRID, Math.round((2 * Math.abs(halfSpan)) / GRID) * GRID);
}

export type FloorPlanBounds = { x: number; y: number; width: number; height: number; };

/** Whether the tool in hand builds its shape corner by corner rather than by dragging a box. */
export function isTracing(state: FloorPlanInteraction): boolean {
  return state.tool === "custom-room"
    || (state.tool === "area" && state.areaDraft.shape === null);
}

/**
 * Where a trace's live end is: the pointer, or the first corner once the pointer is near enough
 * to close there. Returning the first corner exactly is what makes the snap magnetic rather than
 * merely permissive - the preview shows the shape that will actually be committed.
 */
function draftEnd(
  state: FloorPlanInteraction,
  point: FloorPlanPoint,
): { at: FloorPlanPoint; closing: boolean; } {
  const first = state.draft[0];
  if (
    state.draft.length >= 3
    && Math.hypot(first.x - point.x, first.y - point.y) <= CLOSE_PIXELS * state.unitsPerPixel
  ) return { at: first, closing: true };
  return { at: snap(point, state.grid), closing: false };
}

/**
 * Whether the picked shapes can be tiled. Tables only, and none of them pinned: an area is a
 * region of the hall rather than a thing there are several of, and a pinned table is pinned
 * against being multiplied as much as against being moved.
 */
function repeatable(
  selection: FloorPlanSelection,
  locked: readonly string[],
): boolean {
  return selection.tables.length > 0 && selection.areas.length === 0
    && !selection.tables.some((id) => locked.includes(id));
}

/**
 * Where the two repeat handles sit: off the right edge and off the bottom edge of whatever is
 * picked, at the midpoint of each. Outside the selection rather than on it, so they never sit on
 * a table the organizer is trying to grab.
 */
export function repeatControls(
  document: FloorPlanDocument,
  ids: readonly string[],
  clear: number,
): { x: FloorPlanPoint; y: FloorPlanPoint; } | null {
  const bounds = tablesBounds(document, ids);
  if (!bounds) return null;
  return {
    x: {
      x: Math.round(bounds.x + bounds.width + clear),
      y: Math.round(bounds.y + bounds.height / 2),
    },
    y: {
      x: Math.round(bounds.x + bounds.width / 2),
      y: Math.round(bounds.y + bounds.height + clear),
    },
  };
}

/** The repeat handle under the pointer, or null when there is none to grab. */
export function repeatGrabAt(
  document: FloorPlanDocument,
  point: FloorPlanPoint,
  unitsPerPixel: number,
  selection: FloorPlanSelection,
  locked: readonly string[],
): "x" | "y" | null {
  if (!repeatable(selection, locked)) return null;
  const controls = repeatControls(
    document,
    selection.tables,
    REPEAT_CONTROL_GAP_PIXELS * unitsPerPixel,
  );
  if (!controls) return null;
  const reach = HANDLE_PIXELS * unitsPerPixel;
  if (Math.hypot(controls.x.x - point.x, controls.x.y - point.y) <= reach) return "x";
  if (Math.hypot(controls.y.x - point.x, controls.y.y - point.y) <= reach) return "y";
  return null;
}

export type FloorPlanTiling = {
  axis: "x" | "y";
  copies: number;
  /** Centre-to-centre step between one copy and the next. */
  pitch: number;
  /** The unit's own extent along the axis, so the gap between copies is `pitch - extent`. */
  extent: number;
  /** Still choosing the gap, rather than laying copies out at one already chosen. */
  setting: boolean;
  /** Which way the copies march: with the axis, or back against it. */
  direction: 1 | -1;
};

/**
 * How many copies a drag asks for and how far apart they land. Pulling out one more unit's worth
 * of floor buys one more copy, and the reach inside that band spreads them: count and gap both
 * come off the same drag, which is what lets one handle do the whole job.
 */
/**
 * One copy, spaced the way the hall would be filled: the unit's own extent plus the clearance
 * that side of a table needs, rounded up to the grid so the copy lands where a placed table would.
 */
function defaultRepeat(
  document: FloorPlanDocument,
  bounds: FloorPlanBounds,
  axis: "x" | "y",
): FloorPlanTiling {
  const clearance = document.defaults.clearance;
  const extent = axis === "x" ? bounds.width : bounds.height;
  const gap = axis === "x" ? clearance.side * 2 : clearance.front;
  if (extent <= 0) return { axis, copies: 0, pitch: 0, extent, setting: false, direction: 1 };
  return {
    axis,
    copies: 1,
    pitch: Math.ceil((extent + gap) / GRID) * GRID,
    extent,
    setting: false,
    direction: 1,
  };
}

export function repeatTiling(
  bounds: FloorPlanBounds,
  axis: "x" | "y",
  reach: number,
  grid: FloorPlanGrid,
  gap: number | null,
): FloorPlanTiling {
  const extent = axis === "x" ? bounds.width : bounds.height;
  const near = axis === "x" ? bounds.x : bounds.y;
  const far = near + extent;
  /*
   * The copies follow the pointer, so one handle serves both ways: pull it past the far edge and
   * they march out with the axis, pull it back past the near edge and they march against it. The
   * span is always measured from whichever edge the drag is heading away from.
   */
  const direction: 1 | -1 = reach < near ? -1 : 1;
  const span = direction === 1 ? reach - far : near - reach;
  const nothing = { axis, copies: 0, pitch: 0, extent, direction };
  if (extent <= 0) return { ...nothing, setting: gap === null };

  if (gap === null) {
    // Choosing the gap: one copy rides the pointer, so the spacing is something you SEE before
    // you commit to a count. Inside the shape's own footprint there is no gap to read yet.
    if (span <= 0) return { ...nothing, setting: true };
    const stepped = grid.snap ? Math.round(span / grid.size) * grid.size : Math.round(span);
    return {
      axis,
      copies: 1,
      pitch: extent + Math.max(0, stepped),
      extent,
      setting: true,
      direction,
    };
  }

  const pitch = extent + gap;
  return {
    axis,
    copies: pitch > 0 ? Math.max(1, Math.floor(span / pitch)) : 0,
    pitch,
    extent,
    setting: false,
    direction,
  };
}

/**
 * Lays a settled tiling down as tables. One transition, so the whole row is one undo - pulling
 * out eight and then wanting them gone is one keystroke, not eight.
 */
function commitRepeat(
  state: FloorPlanInteraction,
  document: FloorPlanDocument,
  plan: FloorPlanTiling,
): FloorPlanTransition {
  const drag = state.drag;
  const resting = { ...state, drag: null };
  if (drag?.kind !== "repeat-shapes" || !plan.copies) return unchanged(resting, document);
  const next = repeatTables(
    document,
    drag.selection.tables,
    plan.axis,
    plan.copies,
    plan.pitch * plan.direction,
  );
  return { state: resting, document: next, committed: next !== document };
}

/** The gap a drag has reached, for locking in when tiling is armed. */
function settledGap(drag: Extract<FloorPlanDrag, { kind: "repeat-shapes"; }>, grid: FloorPlanGrid) {
  const reach = drag.axis === "x" ? drag.to.x : drag.to.y;
  return Math.max(
    0,
    repeatTiling(drag.bounds, drag.axis, reach, grid, null).pitch
      - (drag.axis === "x" ? drag.bounds.width : drag.bounds.height),
  );
}
/** A drag shorter than this is a click; below it a marquee never starts. */
const CLICK_PIXELS = 4;

export type FloorPlanTool =
  | "select"
  | "room"
  | "custom-room"
  | "table"
  | "area";

/** Everything the organizer can pick up. Areas are objects too, not paint on the floor. */
export type FloorPlanSelection = { tables: string[]; areas: string[]; };

export const EMPTY_SELECTION: FloorPlanSelection = { tables: [], areas: [] };

export function selectionSize(selection: FloorPlanSelection): number {
  return selection.tables.length + selection.areas.length;
}

export type FloorPlanDrag =
  | { kind: "draw-room"; from: FloorPlanPoint; to: FloorPlanPoint; }
  | {
    kind: "move-shapes";
    origin: FloorPlanPoint;
    to: FloorPlanPoint;
    selection: FloorPlanSelection;
  }
  | { kind: "draw-area"; shape: FloorPlanShape; from: FloorPlanPoint; to: FloorPlanPoint; }
  | {
    kind: "repeat-shapes";
    axis: "x" | "y";
    /** The tables being tiled, held so the drag survives a selection change mid-gesture. */
    selection: FloorPlanSelection;
    /** The unit's world rectangle at grab time; the tiling reads its extent, not the document. */
    bounds: FloorPlanBounds;
    /** Where the handle was grabbed, so a press that never travelled still reads as a click. */
    from: FloorPlanPoint;
    to: FloorPlanPoint;
    /**
     * The gap the drag settled on, locked when tiling was armed. Null while the drag is still
     * choosing it: one drag sets one thing, and the count comes after.
     */
    gap: number | null;
    /** Armed with the button already let go, so the count follows the pointer and a click ends it. */
    waiting: boolean;
  }
  | { kind: "move-vertex"; index: number; to: FloorPlanPoint; }
  | {
    kind: "rotate-table";
    id: string;
    center: FloorPlanPoint;
    /** Pointer bearing when the corner was grabbed, and the table's angle at that moment. */
    grabbed: number;
    origin: number;
    /** The snapped angle the drag currently proposes. */
    to: number;
  }
  | {
    kind: "resize-table";
    id: string;
    center: FloorPlanPoint;
    rotation: number;
    /** The table's own width and depth when the handle was grabbed. A corner touches both edges,
     * so one drag moves both dimensions at once. */
    from: { width: number; depth: number; };
    /** The snapped width and depth the drag currently proposes. */
    to: { width: number; depth: number; };
  }
  | {
    kind: "resize-area-vertex";
    areaId: string;
    index: number;
    /** The point the drag currently proposes - snapped to a nearby room edge when there is one,
     * otherwise wherever the pointer is. An area has no fixed width or depth to round to the
     * foot instead. */
    to: FloorPlanPoint;
  }
  | {
    kind: "marquee";
    from: FloorPlanPoint;
    to: FloorPlanPoint;
    /** Shift was held at the press, so the box adds to what was already picked. */
    additive: boolean;
  };

export type FloorPlanInteraction = {
  tool: FloorPlanTool;
  selection: FloorPlanSelection;
  /** Vertices of a polygon being clicked out, for `custom-room` and traced areas. */
  draft: FloorPlanPoint[];
  /**
   * What the area tool will draw next. `shape` null means trace it corner by corner, which stays
   * available for the irregular obstruction a stock shape cannot describe.
   */
  areaDraft: { name: string; obstructs: boolean; shape: FloorPlanShape | null; };
  drag: FloorPlanDrag | null;
  hoverTableId: string | null;
  hoverAreaId: string | null;
  /** Selected table whose rotation ring the pointer is in, and which corner it is beside. */
  hoverRotateTableId: string | null;
  /** Which repeat handle the pointer is over, so the canvas can light it. */
  hoverRepeatAxis: "x" | "y" | null;
  /** Which resize handle (by corner index, 0-3) of the selected table the pointer is over, so the
   * canvas can light it. */
  hoverResizeCorner: number | null;
  /** Which vertex handle of the selected area the pointer is over, so the canvas can light it. */
  hoverAreaVertexIndex: number | null;
  /**
   * Where the pointer is while a shape is being traced out corner by corner, magnetised to the
   * first corner when it is close enough to close there. Null when nothing is being traced.
   */
  draftPointer: FloorPlanPoint | null;
  /**
   * Tables pinned against moving, turning and deletion. Session state, not a document fact: the
   * backend drops unknown document fields, so a persisted lock needs a schema change first.
   */
  locked: readonly string[];
  /** World units per screen pixel; the renderer keeps this current. */
  unitsPerPixel: number;
  /** Spacing every placement rounds to, and whether it rounds to it at all. */
  grid: FloorPlanGrid;
};

export type FloorPlanEvent =
  | { type: "tool"; tool: FloorPlanTool; }
  | { type: "viewport"; unitsPerPixel: number; }
  | { type: "pointerDown"; point: FloorPlanPoint; additive?: boolean; }
  | { type: "pointerMove"; point: FloorPlanPoint; }
  | { type: "pointerUp"; point: FloorPlanPoint; }
  | { type: "closeDraft"; area?: Omit<FloorPlanArea, "polygon" | "id">; }
  | { type: "areaDraft"; areaDraft: Partial<FloorPlanInteraction["areaDraft"]>; }
  | { type: "undoPoint"; }
  | { type: "scaleFirstWall"; length: number; }
  | { type: "grid"; grid: Partial<FloorPlanGrid>; }
  | { type: "lock"; ids: readonly string[]; locked: boolean; }
  | { type: "insertVertex"; point: FloorPlanPoint; }
  | { type: "deleteVertex"; point: FloorPlanPoint; }
  | { type: "select"; selection: FloorPlanSelection; }
  | { type: "selectAll"; }
  | { type: "duplicate"; offset?: FloorPlanPoint; }
  | { type: "delete"; }
  | { type: "nudge"; delta: FloorPlanPoint; }
  | { type: "armRepeat"; }
  | { type: "cancel"; };

export type FloorPlanTransition = {
  state: FloorPlanInteraction;
  document: FloorPlanDocument;
  /** Set when the transition committed a document change worth an undo entry. */
  committed: boolean;
};

export function createInteraction(tool: FloorPlanTool = "select"): FloorPlanInteraction {
  return {
    tool,
    selection: EMPTY_SELECTION,
    areaDraft: { name: "Stage", obstructs: true, shape: "rectangle" },
    draft: [],
    drag: null,
    hoverTableId: null,
    hoverAreaId: null,
    hoverRotateTableId: null,
    hoverRepeatAxis: null,
    hoverResizeCorner: null,
    hoverAreaVertexIndex: null,
    draftPointer: null,
    locked: [],
    unitsPerPixel: 1,
    grid: { ...DEFAULT_GRID },
  };
}

export type FloorPlanGrid = { size: number; snap: boolean; };

export const DEFAULT_GRID: FloorPlanGrid = { size: GRID, snap: true };

/**
 * Rounds to the grid when snapping is on and to whole units when it is off. Coordinates are
 * persisted as integers either way, so switching snapping off loosens where a point may land
 * without letting a fractional one into the document.
 */
export function snap(point: FloorPlanPoint, grid: FloorPlanGrid = DEFAULT_GRID): FloorPlanPoint {
  if (!grid.snap || !(grid.size > 0)) {
    return { x: Math.round(point.x), y: Math.round(point.y) };
  }
  return {
    x: Math.round(point.x / grid.size) * grid.size,
    y: Math.round(point.y / grid.size) * grid.size,
  };
}

/**
 * The boundary edge under `point`, with `point` projected onto it. Double-clicking an edge is how
 * a rectangle becomes an irregular hall, so the hit has to name which edge and exactly where.
 */
export function edgeAt(
  document: FloorPlanDocument,
  point: FloorPlanPoint,
  unitsPerPixel: number,
): { index: number; point: FloorPlanPoint; } | null {
  const boundary = document.boundary;
  if (boundary.length < 2) return null;
  const reach = HANDLE_PIXELS * unitsPerPixel;
  let best: { index: number; point: FloorPlanPoint; distance: number; } | null = null;
  for (let index = 0; index < boundary.length; index += 1) {
    const from = boundary[index];
    const to = boundary[(index + 1) % boundary.length];
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const span = dx * dx + dy * dy;
    if (!span) continue;
    const along = Math.min(
      1,
      Math.max(0, ((point.x - from.x) * dx + (point.y - from.y) * dy) / span),
    );
    const on = { x: Math.round(from.x + dx * along), y: Math.round(from.y + dy * along) };
    const distance = Math.hypot(on.x - point.x, on.y - point.y);
    if (distance <= reach && (!best || distance < best.distance)) {
      best = { index, point: on, distance };
    }
  }
  return best && { index: best.index, point: best.point };
}

export function tableAt(
  document: FloorPlanDocument,
  point: FloorPlanPoint,
): FloorPlanDocument["tables"][number] | null {
  for (let index = document.tables.length - 1; index >= 0; index -= 1) {
    const table = document.tables[index];
    if (pointInPolygon(point, tablePolygon(table))) return table;
  }
  return null;
}

/** Topmost labelled area under a point. Tables sit above areas, so callers test tables first. */
export function areaAt(document: FloorPlanDocument, point: FloorPlanPoint): PlanArea | null {
  for (let index = document.areas.length - 1; index >= 0; index -= 1) {
    const area = document.areas[index];
    if (pointInPolygon(point, area.polygon)) return area;
  }
  return null;
}

function centroid(polygon: readonly FloorPlanPoint[]): FloorPlanPoint {
  return polygon.reduce(
    (sum, point) => ({ x: sum.x + point.x / polygon.length, y: sum.y + point.y / polygon.length }),
    { x: 0, y: 0 },
  );
}

export function vertexAt(
  document: FloorPlanDocument,
  point: FloorPlanPoint,
  unitsPerPixel: number,
): number | null {
  const radius = HANDLE_PIXELS * unitsPerPixel;
  for (let index = 0; index < document.boundary.length; index += 1) {
    const vertex = document.boundary[index];
    if (Math.hypot(vertex.x - point.x, vertex.y - point.y) <= radius) return index;
  }
  return null;
}

/**
 * A rectangular room resizes from the opposite corner, which is what a drag handle means to
 * anyone who has used a drawing tool. Any other polygon moves the single vertex it was given.
 */
export function dragBoundaryVertex(
  boundary: readonly FloorPlanPoint[],
  index: number,
  point: FloorPlanPoint,
): FloorPlanPoint[] {
  if (index < 0 || index >= boundary.length) return [...boundary];
  if (isAxisAlignedRectangle(boundary)) {
    const opposite = boundary[(index + 2) % 4];
    const resized = rectangleBoundary(opposite, point);
    if (resized.length) return resized;
  }
  return boundary.map((vertex, vertexIndex) =>
    vertexIndex === index ? { x: Math.round(point.x), y: Math.round(point.y) } : vertex
  );
}

function nextAreaId(document: FloorPlanDocument): string {
  let number = document.areas.length + 1;
  while (document.areas.some((area) => area.id === `area-${number}`)) number += 1;
  return `area-${number}`;
}

function withSelection(
  state: FloorPlanInteraction,
  selection: FloorPlanSelection,
): FloorPlanInteraction {
  return { ...state, selection };
}

function toggle(ids: readonly string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((current) => current !== id) : [...ids, id];
}

/** Keeps `base` in its existing order and appends only what it does not already hold. */
function union(base: FloorPlanSelection, added: FloorPlanSelection): FloorPlanSelection {
  return {
    tables: [...base.tables, ...added.tables.filter((id) => !base.tables.includes(id))],
    areas: [...base.areas, ...added.areas.filter((id) => !base.areas.includes(id))],
  };
}

/**
 * Filters a selection to the ids `document` actually holds. A selection must only name live
 * shapes, so this runs where selections are installed rather than at each caller. A stale id
 * would open the inspector on an empty title with live Duplicate and Delete, and report a shape
 * the roster cannot assign; copy, delete, paste and undo can each produce one.
 *
 * Returns `selection` itself when nothing is dropped, so an unchanged pick costs no render.
 */
function present(
  selection: FloorPlanSelection,
  document: FloorPlanDocument,
): FloorPlanSelection {
  const liveTables = new Set(document.tables.map((table) => table.id));
  const liveAreas = new Set(document.areas.map((area) => area.id));
  const tables = selection.tables.filter((id) => liveTables.has(id));
  const areas = selection.areas.filter((id) => liveAreas.has(id));
  return tables.length === selection.tables.length && areas.length === selection.areas.length
    ? selection
    : { tables, areas };
}

function unchanged(
  state: FloorPlanInteraction,
  document: FloorPlanDocument,
): FloorPlanTransition {
  return { state, document, committed: false };
}

/**
 * Closes a traced shape on the corner it started from. The area tool takes its label from the
 * draft the panel has been editing, which is the same place the dragged-shape path reads it.
 */
function closeTrace(
  state: FloorPlanInteraction,
  document: FloorPlanDocument,
): FloorPlanTransition {
  const draft = state.draft;
  const resting = { ...state, draft: [], draftPointer: null };
  if (state.tool === "custom-room") {
    return {
      state: { ...resting, tool: "select" },
      document: { ...document, boundary: draft },
      committed: true,
    };
  }
  const { name, obstructs } = state.areaDraft;
  if (!name.trim()) return unchanged(state, document);
  const id = nextAreaId(document);
  const next = addArea(document, { id, name: name.trim(), obstructs, polygon: draft });
  if (next === document) return unchanged(resting, document);
  return {
    state: withSelection(resting, { tables: [], areas: [id] }),
    document: next,
    committed: true,
  };
}

function pointerDown(
  state: FloorPlanInteraction,
  document: FloorPlanDocument,
  event: Extract<FloorPlanEvent, { type: "pointerDown"; }>,
): FloorPlanTransition {
  const raw = event.point;
  const point = snap(raw, state.grid);

  // A repeat waiting on its count is placed by the next press, wherever on the floor it lands.
  const placing = state.drag;
  if (placing?.kind === "repeat-shapes" && placing.waiting) {
    return commitRepeat(
      state,
      document,
      repeatTiling(
        placing.bounds,
        placing.axis,
        placing.axis === "x" ? raw.x : raw.y,
        state.grid,
        placing.gap,
      ),
    );
  }

  // Pressing the first corner again is how a trace ends, and it beats every tool branch below:
  // the click that lands there was aimed at closing the shape, not at dropping another corner.
  if (isTracing(state) && draftEnd(state, raw).closing) {
    return closeTrace(state, document);
  }

  if (state.tool === "room") {
    return unchanged({ ...state, drag: { kind: "draw-room", from: point, to: point } }, document);
  }
  // A drawn control has to answer to the pointer wherever it is drawn, and both of these stay
  // on screen while the table tool is still in hand.
  const tiling = repeatGrabAt(document, raw, state.unitsPerPixel, state.selection, state.locked);
  const unit = tiling ? tablesBounds(document, state.selection.tables) : null;
  if (tiling && unit) {
    return unchanged({
      ...state,
      drag: {
        kind: "repeat-shapes",
        axis: tiling,
        selection: state.selection,
        bounds: unit,
        from: raw,
        to: raw,
        gap: null,
        waiting: false,
      },
    }, document);
  }

  const turning = rotationGrabAt(
    document,
    raw,
    state.unitsPerPixel,
    unpinned(state, state.selection.tables),
  );
  if (turning) {
    return unchanged({
      ...state,
      drag: {
        kind: "rotate-table",
        id: turning.id,
        center: turning.center,
        grabbed: bearing(turning.center, raw),
        origin: turning.rotation,
        to: turning.rotation,
      },
    }, document);
  }

  const resizing = resizeGrabAt(
    document,
    raw,
    state.unitsPerPixel,
    unpinned(state, state.selection.tables),
  );
  if (resizing) {
    const from = { width: resizing.table.width, depth: resizing.table.depth };
    return unchanged({
      ...state,
      drag: {
        kind: "resize-table",
        id: resizing.table.id,
        center: resizing.table.center,
        rotation: resizing.table.rotation,
        from,
        to: from,
      },
    }, document);
  }

  const areaVertex = areaVertexGrabAt(document, raw, state.unitsPerPixel, state.selection);
  if (areaVertex) {
    return unchanged({
      ...state,
      drag: {
        kind: "resize-area-vertex",
        areaId: areaVertex.area.id,
        index: areaVertex.index,
        to: areaVertex.area.polygon[areaVertex.index],
      },
    }, document);
  }

  if (state.tool === "table") {
    /*
     * A table already on the floor is something to grab, not a spot to stack a second one on, so
     * pressing one selects and arms the same move drag the select tool would. The tool stays
     * armed either way: the next press on open floor still places.
     */
    const standing = tableAt(document, raw);
    if (standing) {
      const already = state.selection.tables.includes(standing.id);
      const selection: FloorPlanSelection = event.additive
        ? { ...state.selection, tables: toggle(state.selection.tables, standing.id) }
        : already
        ? state.selection
        : { tables: [standing.id], areas: [] };
      return unchanged(
        {
          ...state,
          selection,
          drag: selectionSize(selection) && !isLocked(state, selection)
            ? { kind: "move-shapes", origin: point, to: point, selection }
            : null,
        },
        document,
      );
    }
    const placed = addTable(document, point);
    if (placed === document) return unchanged(state, document);
    const added = placed.tables.at(-1);
    return {
      state: withSelection(state, { tables: added ? [added.id] : [], areas: [] }),
      document: placed,
      committed: true,
    };
  }
  if (state.tool === "area") {
    /*
     * An area already on the floor is something to grab, not a spot to draw a second one over,
     * so pressing one selects it and arms the same move drag the select tool would - the same
     * bargain the table tool strikes. Mid-trace it still drops a corner: a click inside a stage
     * while outlining the next one is a corner, not a change of mind.
     */
    const standing = state.draft.length ? null : areaAt(document, raw);
    if (standing) {
      const selection: FloorPlanSelection = event.additive
        ? { ...state.selection, areas: toggle(state.selection.areas, standing.id) }
        : state.selection.areas.includes(standing.id)
        ? state.selection
        : { tables: [], areas: [standing.id] };
      return unchanged(
        {
          ...state,
          selection,
          drag: selectionSize(selection)
            ? { kind: "move-shapes", origin: point, to: point, selection }
            : null,
        },
        document,
      );
    }
    const shape = state.areaDraft.shape;
    return unchanged(
      shape
        ? { ...state, drag: { kind: "draw-area", shape, from: point, to: point } }
        : { ...state, draft: [...state.draft, point] },
      document,
    );
  }
  if (state.tool === "custom-room") {
    return unchanged({ ...state, draft: [...state.draft, point] }, document);
  }

  const vertex = vertexAt(document, raw, state.unitsPerPixel);
  if (vertex !== null) {
    return unchanged(
      {
        ...state,
        selection: EMPTY_SELECTION,
        drag: { kind: "move-vertex", index: vertex, to: point },
      },
      document,
    );
  }

  const table = tableAt(document, raw);
  const area = table ? null : areaAt(document, raw);
  if (!table && !area) {
    return unchanged(
      {
        ...state,
        selection: event.additive ? state.selection : EMPTY_SELECTION,
        drag: { kind: "marquee", from: raw, to: raw, additive: event.additive === true },
      },
      document,
    );
  }

  const hitId = table?.id ?? area?.id ?? "";
  const key = table ? "tables" : "areas";
  const already = state.selection[key].includes(hitId);
  const selection: FloorPlanSelection = event.additive
    ? { ...state.selection, [key]: toggle(state.selection[key], hitId) }
    : already
    ? state.selection
    : { tables: [], areas: [], [key]: [hitId] };
  return unchanged(
    {
      ...state,
      selection,
      drag: selectionSize(selection) && !isLocked(state, selection)
        ? { kind: "move-shapes", origin: point, to: point, selection }
        : null,
    },
    document,
  );
}

/** Locked tables are excluded wholesale: a mixed selection moves nothing until it is unlocked. */
function isLocked(state: FloorPlanInteraction, selection: FloorPlanSelection): boolean {
  return selection.tables.some((id) => state.locked.includes(id));
}

function unpinned(state: FloorPlanInteraction, ids: readonly string[]): readonly string[] {
  return ids.filter((id) => !state.locked.includes(id));
}

function pointerMove(
  state: FloorPlanInteraction,
  document: FloorPlanDocument,
  point: FloorPlanPoint,
): FloorPlanTransition {
  if (!state.drag) {
    // The controls are drawn under the table tool as well, so they light up under it as well.
    const holding = state.tool === "select" || state.tool === "table";
    const overRing = holding
      ? rotationGrabAt(document, point, state.unitsPerPixel, state.selection.tables)
      : null;
    const overResize = holding && !overRing
      ? resizeGrabAt(document, point, state.unitsPerPixel, state.selection.tables)
      : null;
    const overAreaVertex = holding && !overRing && !overResize
      ? areaVertexGrabAt(document, point, state.unitsPerPixel, state.selection)
      : null;
    const hoverRepeatAxis = holding && !overRing && !overResize && !overAreaVertex
      ? repeatGrabAt(document, point, state.unitsPerPixel, state.selection, state.locked)
      : null;
    const grabbing = overRing !== null || overResize !== null || overAreaVertex !== null
      || hoverRepeatAxis !== null;
    const hit = state.tool === "select" && !grabbing ? tableAt(document, point) : null;
    const overArea = state.tool === "select" && !hit && !grabbing ? areaAt(document, point) : null;
    const hoverTableId = hit?.id ?? null;
    const hoverAreaId = overArea?.id ?? null;
    const hoverRotateTableId = overRing?.id ?? null;
    const hoverResizeCorner = overResize?.corner ?? null;
    const hoverAreaVertexIndex = overAreaVertex?.index ?? null;
    const draftPointer = isTracing(state) && state.draft.length
      ? draftEnd(state, point).at
      : null;
    return hoverTableId === state.hoverTableId && hoverAreaId === state.hoverAreaId
        && hoverRotateTableId === state.hoverRotateTableId
        && hoverRepeatAxis === state.hoverRepeatAxis
        && hoverResizeCorner === state.hoverResizeCorner
        && hoverAreaVertexIndex === state.hoverAreaVertexIndex
        && draftPointer?.x === state.draftPointer?.x
        && draftPointer?.y === state.draftPointer?.y
      ? unchanged(state, document)
      : unchanged(
        {
          ...state,
          hoverTableId,
          hoverAreaId,
          hoverRotateTableId,
          hoverRepeatAxis,
          hoverResizeCorner,
          hoverAreaVertexIndex,
          draftPointer,
        },
        document,
      );
  }
  if (state.drag.kind === "rotate-table") {
    const swept = state.drag.origin + bearing(state.drag.center, point) - state.drag.grabbed;
    const to = snapRowAngle(swept);
    return to === state.drag.to
      ? unchanged(state, document)
      : unchanged({ ...state, drag: { ...state.drag, to } }, document);
  }
  if (state.drag.kind === "resize-table") {
    const offset = localOffset(state.drag.center, state.drag.rotation, point);
    const to = { width: snappedSpan(offset.across), depth: snappedSpan(offset.forward) };
    return to.width === state.drag.to.width && to.depth === state.drag.to.depth
      ? unchanged(state, document)
      : unchanged({ ...state, drag: { ...state.drag, to } }, document);
  }
  if (state.drag.kind === "resize-area-vertex") {
    // Free-form, unlike a table's whole-foot span: only a nearby room edge pulls it, never the grid.
    const to = edgeAt(document, point, state.unitsPerPixel)?.point ?? point;
    return to.x === state.drag.to.x && to.y === state.drag.to.y
      ? unchanged(state, document)
      : unchanged({ ...state, drag: { ...state.drag, to } }, document);
  }
  const snapped = snap(point, state.grid);
  // A marquee sweeps screen space, and a repeat reads its pitch off the raw reach — snapping the
  // pointer there would quantise the count as well as the gap.
  const drag = state.drag.kind === "marquee" || state.drag.kind === "repeat-shapes"
    ? { ...state.drag, to: point }
    : { ...state.drag, to: snapped };
  return unchanged({ ...state, drag }, document);
}

function pointerUp(
  state: FloorPlanInteraction,
  document: FloorPlanDocument,
  point: FloorPlanPoint,
): FloorPlanTransition {
  const drag = state.drag;
  if (!drag) return unchanged(state, document);
  const resting = { ...state, drag: null };

  if (drag.kind === "draw-room") {
    const boundary = rectangleBoundary(drag.from, snap(point, state.grid));
    if (!boundary.length) return unchanged(resting, document);
    return {
      state: { ...resting, tool: "select" },
      document: { ...document, boundary },
      committed: true,
    };
  }

  if (drag.kind === "draw-area") {
    const polygon = shapePolygon(drag.shape, drag.from, snap(point, state.grid));
    if (polygon.length < 3) return unchanged(resting, document);
    const { name, obstructs } = state.areaDraft;
    if (!name.trim()) return unchanged(resting, document);
    const id = nextAreaId(document);
    const next = addArea(document, { id, name: name.trim(), obstructs, polygon });
    if (next === document) return unchanged(resting, document);
    return {
      state: withSelection(resting, { tables: [], areas: [id] }),
      document: next,
      committed: true,
    };
  }

  if (drag.kind === "repeat-shapes") {
    /*
     * Letting go ENDS the gap and begins the count: the pointer keeps driving with the button
     * up, and a click places the copies. Holding a button down across a long pull is the part of
     * this gesture that was uncomfortable, and it is the part nobody has to do now. Ctrl still
     * arms it mid-drag for anyone who would rather not let go.
     */
    if (drag.gap === null) {
      const travelled = Math.hypot(point.x - drag.from.x, point.y - drag.from.y);
      /*
       * Pressing the handle without pulling it asks for one more, at the spacing Fill the hall
       * would use. It is a plus sign, and doing nothing there reads as a broken control.
       * Measured from where the handle was GRABBED, because the handle already stands clear of
       * the shape - reading the gap alone would call that standoff a deliberate pull.
       */
      if (travelled <= CLICK_PIXELS * state.unitsPerPixel) {
        return commitRepeat(state, document, defaultRepeat(document, drag.bounds, drag.axis));
      }
      const settled = settledGap({ ...drag, to: point }, state.grid);
      return unchanged({ ...state, drag: { ...drag, gap: settled, waiting: true } }, document);
    }
    return commitRepeat(
      state,
      document,
      repeatTiling(
        drag.bounds,
        drag.axis,
        drag.axis === "x" ? point.x : point.y,
        state.grid,
        drag.gap,
      ),
    );
  }

  if (drag.kind === "move-shapes") {
    const to = snap(point, state.grid);
    const delta = { x: to.x - drag.origin.x, y: to.y - drag.origin.y };
    const next = moveAreas(
      moveTables(document, drag.selection.tables, delta),
      drag.selection.areas,
      delta,
    );
    return { state: resting, document: next, committed: next !== document };
  }

  if (drag.kind === "rotate-table") {
    if (drag.to === drag.origin) return unchanged(resting, document);
    return {
      state: resting,
      document: rotateTable(document, drag.id, drag.to - drag.origin),
      committed: true,
    };
  }

  if (drag.kind === "resize-table") {
    if (drag.to.width === drag.from.width && drag.to.depth === drag.from.depth) {
      return unchanged(resting, document);
    }
    return {
      state: resting,
      document: resizeTables(document, [drag.id], drag.to),
      committed: true,
    };
  }

  if (drag.kind === "resize-area-vertex") {
    const next = moveAreaVertex(document, drag.areaId, drag.index, drag.to);
    return next === document ? unchanged(resting, document) : {
      state: resting,
      document: next,
      committed: true,
    };
  }

  if (drag.kind === "move-vertex") {
    const boundary = dragBoundaryVertex(document.boundary, drag.index, snap(point, state.grid));
    // A click that merely lands on a handle must not enter the undo history.
    const moved = boundary.some((vertex, index) =>
      vertex.x !== document.boundary[index]?.x || vertex.y !== document.boundary[index]?.y
    ) || boundary.length !== document.boundary.length;
    if (!moved) {
      return unchanged(resting, document);
    }
    return { state: resting, document: { ...document, boundary }, committed: true };
  }

  const left = Math.min(drag.from.x, point.x);
  const right = Math.max(drag.from.x, point.x);
  const top = Math.min(drag.from.y, point.y);
  const bottom = Math.max(drag.from.y, point.y);
  const travel = Math.hypot(right - left, bottom - top) / state.unitsPerPixel;
  if (travel < CLICK_PIXELS) return unchanged(resting, document);
  const within = (candidate: FloorPlanPoint) =>
    candidate.x >= left && candidate.x <= right && candidate.y >= top && candidate.y <= bottom;
  const enclosed: FloorPlanSelection = {
    tables: document.tables.filter((table) => within(table.center)).map((table) => table.id),
    areas: document.areas.filter((area) => within(centroid(area.polygon))).map((area) => area.id),
  };
  // `pointerDown` kept the running selection for an additive press. Merging it here lets a block
  // be built in two passes instead of the second box discarding the first.
  return unchanged(
    withSelection(resting, drag.additive ? union(state.selection, enclosed) : enclosed),
    document,
  );
}

export function reduceFloorPlan(
  state: FloorPlanInteraction,
  document: FloorPlanDocument,
  event: FloorPlanEvent,
): FloorPlanTransition {
  switch (event.type) {
    case "tool":
      return unchanged(
        {
          ...state,
          tool: event.tool,
          // A drawing tool's panel describes what it is about to make, so arming one clears the
          // selection; otherwise the shape library stays hidden behind a table inspector. Only
          // the select tool keeps a selection.
          selection: event.tool === "select" ? state.selection : EMPTY_SELECTION,
          draft: [],
          draftPointer: null,
          drag: null,
          hoverTableId: null,
          hoverAreaId: null,
        },
        document,
      );

    case "viewport":
      return event.unitsPerPixel === state.unitsPerPixel
        ? unchanged(state, document)
        : unchanged({ ...state, unitsPerPixel: event.unitsPerPixel }, document);

    case "pointerDown":
      return pointerDown(state, document, event);

    case "pointerMove":
      return pointerMove(state, document, event.point);

    case "pointerUp":
      return pointerUp(state, document, event.point);

    case "closeDraft": {
      if (state.draft.length < 3) return unchanged(state, document);
      const draft = state.draft;
      if (state.tool === "custom-room") {
        return {
          state: { ...state, draft: [], draftPointer: null, tool: "select" },
          document: { ...document, boundary: draft },
          committed: true,
        };
      }
      if (!event.area) return unchanged(state, document);
      const next = addArea(document, { ...event.area, id: nextAreaId(document), polygon: draft });
      return {
        state: { ...state, draft: [], draftPointer: null },
        document: next,
        committed: next !== document,
      };
    }

    case "areaDraft":
      return unchanged(
        { ...state, areaDraft: { ...state.areaDraft, ...event.areaDraft } },
        document,
      );

    case "undoPoint":
      return state.draft.length
        ? unchanged({ ...state, draft: state.draft.slice(0, -1) }, document)
        : unchanged(state, document);

    /*
     * Naming the first wall's real length is how a traced outline acquires scale. Only the second
     * point moves, along the line the two already describe, so the wall the Organizer drew keeps
     * its direction and its start. Safe precisely because nothing else exists yet: with two draft
     * points and no committed geometry, there is nothing a resize could knock out of true.
     */
    case "scaleFirstWall": {
      if (state.draft.length !== 2 || !(event.length > 0)) return unchanged(state, document);
      const [from, to] = state.draft;
      const span = Math.hypot(to.x - from.x, to.y - from.y);
      if (span < 1) return unchanged(state, document);
      const ratio = event.length / span;
      return unchanged({
        ...state,
        draft: [from, {
          x: Math.round(from.x + (to.x - from.x) * ratio),
          y: Math.round(from.y + (to.y - from.y) * ratio),
        }],
      }, document);
    }

    case "grid":
      return unchanged({ ...state, grid: { ...state.grid, ...event.grid } }, document);

    case "lock": {
      const pinned = new Set(state.locked);
      for (const id of event.ids) {
        if (event.locked) pinned.add(id);
        else pinned.delete(id);
      }
      return unchanged({ ...state, locked: [...pinned] }, document);
    }

    /* A rectangle is the fast way in; these two turn it into the hall the venue actually is. */
    case "insertVertex": {
      const edge = edgeAt(document, event.point, state.unitsPerPixel);
      if (!edge) return unchanged(state, document);
      const boundary = [...document.boundary];
      // Deliberately unsnapped: a new node belongs ON the wall it was added to, not beside it.
      boundary.splice(edge.index + 1, 0, edge.point);
      return { state, document: { ...document, boundary }, committed: true };
    }

    case "deleteVertex": {
      const index = vertexAt(document, event.point, state.unitsPerPixel);
      if (index === null || document.boundary.length <= 3) return unchanged(state, document);
      return {
        state,
        document: { ...document, boundary: document.boundary.filter((_, at) => at !== index) },
        committed: true,
      };
    }

    case "select": {
      const selection = present(event.selection, document);
      return unchanged(
        selection === state.selection ? state : withSelection(state, selection),
        document,
      );
    }

    case "selectAll":
      return unchanged(
        withSelection(state, {
          tables: document.tables.map((table) => table.id),
          areas: document.areas.map((area) => area.id),
        }),
        document,
      );

    case "duplicate": {
      if (!selectionSize(state.selection)) return unchanged(state, document);
      const offset = event.offset ?? { x: GRID, y: GRID };
      const tables = duplicateTables(document, state.selection.tables, offset);
      const areas = duplicateAreas(tables.document, state.selection.areas, offset);
      return {
        state: withSelection(state, { tables: tables.ids, areas: areas.ids }),
        document: areas.document,
        committed: tables.ids.length + areas.ids.length > 0,
      };
    }

    case "delete": {
      if (!selectionSize(state.selection)) return unchanged(state, document);
      const next = removeAreas(
        removeTables(document, unpinned(state, state.selection.tables)),
        state.selection.areas,
      );
      return {
        state: withSelection(state, EMPTY_SELECTION),
        document: next,
        committed: next !== document,
      };
    }

    case "nudge": {
      if (!selectionSize(state.selection)) return unchanged(state, document);
      const next = moveAreas(
        moveTables(document, state.selection.tables, event.delta),
        state.selection.areas,
        event.delta,
      );
      return { state, document: next, committed: next !== document };
    }

    /*
     * Locks the gap the drag has reached and starts laying copies out at it. A latch, not a held
     * key: having to keep a finger down for the rest of a drag is a way to lose the drag.
     */
    case "armRepeat": {
      const arming = state.drag;
      if (arming?.kind !== "repeat-shapes" || arming.gap !== null) {
        return unchanged(state, document);
      }
      // Armed without letting go, so the button still ends it.
      return unchanged(
        { ...state, drag: { ...arming, gap: settledGap(arming, state.grid), waiting: false } },
        document,
      );
    }

    case "cancel":
      return unchanged(
        {
          ...state,
          draft: [],
          draftPointer: null,
          drag: null,
          // Abandoning a repeat drops the copies, not the thing being copied: the organizer is
          // still holding the tables they meant to tile.
          selection: state.drag?.kind === "repeat-shapes" ? state.selection : EMPTY_SELECTION,
        },
        document,
      );

    default:
      return unchanged(state, document);
  }
}

/**
 * The transient shapes a renderer paints on top of the committed document. Everything is in world
 * coordinates, so an SVG and a canvas renderer consume it identically.
 */
export type FloorPlanOverlay = {
  /** Rectangle preview while the room tool is dragging. */
  roomPreview: FloorPlanPoint[] | null;
  /** Live outline of a stock shape being dragged out. */
  areaPreview: FloorPlanPoint[] | null;
  /** Where the repeat handles are drawn, or null when what is picked cannot be tiled. */
  repeatControls: { x: FloorPlanPoint; y: FloorPlanPoint; } | null;
  /** Live tiling while a repeat handle is dragging; the document itself is untouched. */
  repeating:
    | ({ selection: FloorPlanSelection; waiting: boolean; } & FloorPlanTiling)
    | null;
  /** Vertices clicked so far for a custom room or area. */
  draft: FloorPlanPoint[];
  /** Dashed segment from the last corner dropped to wherever the pointer is now. */
  draftRubber: [FloorPlanPoint, FloorPlanPoint] | null;
  /** Quiet dashed hint from the last corner back to the first, showing the way out of the trace. */
  draftGuide: [FloorPlanPoint, FloorPlanPoint] | null;
  /** The shape a click would commit, once the pointer is magnetised to the first corner. */
  draftClosing: FloorPlanPoint[] | null;
  /** Marquee rectangle in world coordinates. */
  marquee: FloorPlanPoint[] | null;
  /** Draggable boundary vertices, shown only when the select tool is active. */
  handles: FloorPlanPoint[];
  /** Live boundary while a vertex handle is dragging; the document itself is untouched. */
  boundaryPreview: FloorPlanPoint[] | null;
  /** Live offset applied to the selected shapes mid-drag; the document itself is untouched. */
  moving: { selection: FloorPlanSelection; delta: FloorPlanPoint; } | null;
  /**
   * Grid intersection the current drag is snapping to, or null when nothing is dragging or the
   * drag does not snap. Showing it tells the Organizer where the drag will land before release.
   */
  snapPoint: FloorPlanPoint | null;
  /** Live angle while a corner is being dragged; the document itself is untouched. */
  rotating: { id: string; rotation: number; } | null;
  /** Live width/depth while a resize handle is dragging; the document itself is untouched. */
  resizing: { id: string; width: number; depth: number; } | null;
  /** Live position of one corner while an area's own vertex handle is dragging; the document
   * itself is untouched. */
  resizingAreaVertex: { areaId: string; index: number; to: FloorPlanPoint; } | null;
  selection: FloorPlanSelection;
  hoverTableId: string | null;
  hoverAreaId: string | null;
  hoverRotateTableId: string | null;
  hoverRepeatAxis: "x" | "y" | null;
  /** Which resize handle (by corner index, 0-3) the pointer is over, so the canvas can light it. */
  hoverResizeCorner: number | null;
  /** Which vertex handle of the selected area the pointer is over, so the canvas can light it. */
  hoverAreaVertexIndex: number | null;
  /**
   * Where the rotate control is drawn and which way its glyph faces, or null when no single
   * unlocked table is picked. Both read the LIVE table — the one under the pointer mid-gesture —
   * so the control rides the table it belongs to instead of sitting where the table used to be.
   */
  rotateControl: { at: FloorPlanPoint; angle: number; } | null;
  /** Where the four resize handles are drawn (the table's own corners), reading the same LIVE
   * table as `rotateControl`, or null when no single unlocked table is picked. */
  resizeControls: readonly FloorPlanPoint[] | null;
  /** Where the selected area's own vertex handles are drawn - its live polygon, one handle per
   * corner - or null when no single area (alone, no table alongside) is picked. */
  areaResizeControls: readonly FloorPlanPoint[] | null;
  /** Tables pinned in place, so the canvas can mark them. */
  locked: readonly string[];
};

/** Whether the traced shape is one click from closing, with the pointer held on its first corner. */
function closingDraft(state: FloorPlanInteraction): boolean {
  const first = state.draft[0];
  const end = state.draftPointer;
  return !!(state.draft.length >= 3 && end && end.x === first.x && end.y === first.y);
}

export function floorPlanOverlay(
  state: FloorPlanInteraction,
  document: FloorPlanDocument,
): FloorPlanOverlay {
  const drag = state.drag;
  return {
    roomPreview: drag?.kind === "draw-room" ? rectangleBoundary(drag.from, drag.to) : null,
    areaPreview: drag?.kind === "draw-area" ? shapePolygon(drag.shape, drag.from, drag.to) : null,
    repeatControls: !drag && repeatable(state.selection, state.locked)
      ? repeatControls(
        document,
        state.selection.tables,
        REPEAT_CONTROL_GAP_PIXELS * state.unitsPerPixel,
      )
      : null,
    repeating: drag?.kind === "repeat-shapes"
      ? {
        selection: drag.selection,
        waiting: drag.waiting,
        ...repeatTiling(
          drag.bounds,
          drag.axis,
          drag.axis === "x" ? drag.to.x : drag.to.y,
          state.grid,
          drag.gap,
        ),
      }
      : null,
    draft: state.draft,
    draftRubber: state.draft.length && state.draftPointer
      ? [state.draft[state.draft.length - 1], state.draftPointer]
      : null,
    // Suppressed while closing, where the rubber band already runs back to the first corner.
    draftGuide: state.draft.length >= 2 && !closingDraft(state)
      ? [state.draft[state.draft.length - 1], state.draft[0]]
      : null,
    draftClosing: closingDraft(state) ? state.draft : null,
    marquee: drag?.kind === "marquee" ? rectangleBoundary(drag.from, drag.to) : null,
    handles: state.tool === "select" && !drag ? document.boundary : [],
    boundaryPreview: drag?.kind === "move-vertex"
      ? dragBoundaryVertex(document.boundary, drag.index, drag.to)
      : null,
    moving: drag?.kind === "move-shapes"
      ? {
        selection: drag.selection,
        delta: { x: drag.to.x - drag.origin.x, y: drag.to.y - drag.origin.y },
      }
      : null,
    // A marquee selects rather than places, and a rotation turns or a resize spans rather than
    // travelling, so none of the three has a grid intersection to report.
    snapPoint: drag && drag.kind !== "marquee" && drag.kind !== "rotate-table"
        && drag.kind !== "resize-table" && drag.kind !== "repeat-shapes"
      ? drag.to
      : null,
    rotating: drag?.kind === "rotate-table" ? { id: drag.id, rotation: drag.to } : null,
    resizing: drag?.kind === "resize-table"
      ? { id: drag.id, width: drag.to.width, depth: drag.to.depth }
      : null,
    resizingAreaVertex: drag?.kind === "resize-area-vertex"
      ? { areaId: drag.areaId, index: drag.index, to: drag.to }
      : null,
    selection: state.selection,
    hoverTableId: state.hoverTableId,
    hoverAreaId: state.hoverAreaId,
    hoverRotateTableId: state.hoverRotateTableId,
    hoverRepeatAxis: state.hoverRepeatAxis,
    hoverResizeCorner: state.hoverResizeCorner,
    hoverAreaVertexIndex: state.hoverAreaVertexIndex,
    rotateControl: (() => {
      const live = liveSingleTable(state, document, drag);
      if (!live) return null;
      return {
        at: rotationControl(live, CONTROL_GAP_PIXELS * state.unitsPerPixel),
        angle: live.rotation,
      };
    })(),
    resizeControls: (() => {
      const live = liveSingleTable(state, document, drag);
      return live && resizeControls(live);
    })(),
    areaResizeControls: liveSingleArea(state, document, drag)?.polygon ?? null,
    locked: state.locked,
  };
}

/**
 * The single selected, unlocked table, adjusted for whatever it is being dragged through right
 * now - turned, shifted, or resized - so the rotate and resize controls both ride the table they
 * belong to instead of sitting where it used to be. Null whenever there is no one table for a
 * control to belong to: several picked, none picked, or the one picked is locked.
 */
function liveSingleTable(
  state: FloorPlanInteraction,
  document: FloorPlanDocument,
  drag: FloorPlanDrag | null,
): FloorPlanTable | null {
  if (state.selection.tables.length !== 1) return null;
  const only = document.tables.find((table) => table.id === state.selection.tables[0]);
  if (!only || state.locked.includes(only.id)) return null;
  const turning = drag?.kind === "rotate-table" && drag.id === only.id;
  const shifting = drag?.kind === "move-shapes" && drag.selection.tables.includes(only.id);
  const resizingDrag = drag?.kind === "resize-table" && drag.id === only.id;
  return {
    ...only,
    rotation: turning ? drag.to : only.rotation,
    center: shifting
      ? {
        x: only.center.x + drag.to.x - drag.origin.x,
        y: only.center.y + drag.to.y - drag.origin.y,
      }
      : only.center,
    width: resizingDrag ? drag.to.width : only.width,
    depth: resizingDrag ? drag.to.depth : only.depth,
  };
}

/**
 * The single selected area, adjusted for whatever it is being dragged through right now - shifted
 * as a whole, or one corner reshaped - so its vertex handles ride the shape they belong to instead
 * of sitting where it used to be. Null whenever there is no one area for a handle to belong to:
 * several picked, none picked, or a table picked alongside it.
 */
function liveSingleArea(
  state: FloorPlanInteraction,
  document: FloorPlanDocument,
  drag: FloorPlanDrag | null,
): FloorPlanArea | null {
  if (state.selection.areas.length !== 1 || state.selection.tables.length !== 0) return null;
  const only = document.areas.find((area) => area.id === state.selection.areas[0]);
  if (!only) return null;
  const shifting = drag?.kind === "move-shapes" && drag.selection.areas.includes(only.id);
  const resizingVertex = drag?.kind === "resize-area-vertex" && drag.areaId === only.id;
  const delta = shifting ? { x: drag.to.x - drag.origin.x, y: drag.to.y - drag.origin.y } : null;
  if (resizingVertex) {
    return { ...only, polygon: reshapedAreaPolygon(only.polygon, drag.index, drag.to) };
  }
  return {
    ...only,
    polygon: delta
      ? only.polygon.map((corner) => ({ x: corner.x + delta.x, y: corner.y + delta.y }))
      : only.polygon,
  };
}
